import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBusinessProfiles,
  listBusinesses,
  setActiveBusiness,
  type BusinessProfilesResponse,
  type SetActiveBusinessResponse,
} from "../../services/businessProfileService";
import {
  businessProfilesQueryKey,
  EMPTY_BUSINESS_PROFILES,
  enrichBusinessProfiles,
  normalizeBusinessList,
  normalizeBusinessProfilesPayload,
  syncActiveBusinessContext,
} from "./businessProfiles";
import { assertBusinessAccess } from "../../utils/businessAccess";
import {
  cancelDposSession,
  createDposSession,
  exitDposCashierMode,
  getAcceptedCurrencies,
  getDposCashierModeStatus,
  getDposPerformance,
  getDposSessionReceipt,
  getDposSessionQr,
  listDposSessions,
  setupDposCashierPin,
  updateAcceptedCurrency,
  verifyDposCashierPin,
  type CreateDposSessionRequest,
  type DposAcceptedCurrency,
  type DposCashierModeStatus,
  type DposPerformanceResponse,
  type DposSessionReceipt,
  type DposSessionListResponse,
  type ExitDposCashierModeRequest,
  type ListDposSessionsParams,
  type SetupDposCashierPinRequest,
  type UpdateAcceptedCurrencyRequest,
  type VerifyDposCashierPinRequest,
} from "../../services/dposService";

export const useBusinessProfiles = () => {
  return useQuery({
    queryKey: businessProfilesQueryKey,
    queryFn: async () => {
      const [profilesResponse, businessListResponse] = await Promise.all([
        getBusinessProfiles().catch(() => null),
        listBusinesses().catch(() => null),
      ]);

      const profiles = normalizeBusinessProfilesPayload(profilesResponse);
      const businessList = normalizeBusinessList(businessListResponse);

      return enrichBusinessProfiles(profiles, businessList);
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useBusinessList = () => {
  const query = useBusinessProfiles();

  return {
    ...query,
    data: query.data?.businesses || [],
  };
};

export const useActiveBusiness = () => {
  const { data, isLoading, error } = useBusinessProfiles();
  const businesses = data?.businesses || [];
  const activeBusinessId = data?.activeBusinessId || null;
  const activeBusiness =
    businesses.find((entry) => entry.businessId === activeBusinessId) || null;

  return {
    personal: data?.personal || EMPTY_BUSINESS_PROFILES.personal,
    businesses,
    activeBusinessId,
    business: activeBusiness,
    businessId: activeBusiness?.businessId || null,
    hasAvailableBusinesses: businesses.length > 0,
    hasSelectedBusiness: Boolean(activeBusiness?.businessId),
    selectionRequired: businesses.length > 0 && !activeBusiness?.businessId,
    isLoading,
    error,
  };
};

export const useSetActiveBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (businessId: string | null) => {
      const cachedProfiles = queryClient.getQueryData<BusinessProfilesResponse>(
        businessProfilesQueryKey
      );
      if (cachedProfiles) {
        assertBusinessAccess(businessId, cachedProfiles.businesses);
      }
      return setActiveBusiness({ businessId });
    },
    onSuccess: async (payload: SetActiveBusinessResponse) => {
      await syncActiveBusinessContext(
        queryClient,
        payload.activeBusinessId ?? null
      );
    },
  });
};

export const useAcceptedCurrencies = (businessId: string | null) => {
  return useQuery({
    queryKey: ["acceptedCurrencies", businessId],
    queryFn: async () => {
      if (!businessId) return [] as DposAcceptedCurrency[];
      return getAcceptedCurrencies(businessId);
    },
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateAcceptedCurrency = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAcceptedCurrencyRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      return updateAcceptedCurrency(businessId, data);
    },
    onSuccess: (currencies) => {
      queryClient.setQueryData(["acceptedCurrencies", businessId], currencies);
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ["acceptedCurrencies", businessId],
      });
    },
  });
};

export const useCreateDposSession = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (data: CreateDposSessionRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      return createDposSession(businessId, data);
    },
  });
};

const DPOS_SESSION_QR_POLL_MS = 10_000;

export const useDposSessionQr = (
  businessId: string | null,
  sessionId: string | null
) => {
  return useQuery({
    queryKey: ["dposSessionQr", businessId, sessionId],
    queryFn: async () => {
      if (!businessId || !sessionId) return null;
      return getDposSessionQr(businessId, sessionId);
    },
    enabled: Boolean(businessId) && Boolean(sessionId),
    staleTime: DPOS_SESSION_QR_POLL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === "paid" ||
        status === "confirmed" ||
        status === "expired" ||
        status === "cancelled" ||
        status === "failed"
      ) {
        return false;
      }
      return DPOS_SESSION_QR_POLL_MS;
    },
  });
};

export const useDposSessionReceipt = (
  businessId: string | null,
  sessionId: string | null
) => {
  return useQuery({
    queryKey: ["dposSessionReceipt", businessId, sessionId],
    queryFn: async () => {
      if (!businessId || !sessionId) {
        return null as DposSessionReceipt | null;
      }

      return getDposSessionReceipt(businessId, sessionId);
    },
    enabled: Boolean(businessId) && Boolean(sessionId),
    staleTime: 1000 * 60,
  });
};

export const useDposSessions = (
  businessId: string | null,
  params: ListDposSessionsParams = {}
) => {
  return useQuery({
    queryKey: ["dposSessions", businessId, params],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 20,
          results: [],
        } as DposSessionListResponse;
      }
      return listDposSessions(businessId, params);
    },
    enabled: Boolean(businessId),
    staleTime: 1000 * 30,
  });
};

export const useDposPerformance = (
  businessId: string | null,
  period = "1w"
) => {
  return useQuery({
    queryKey: ["dposPerformance", businessId, period],
    queryFn: async () => {
      if (!businessId) return null as DposPerformanceResponse | null;
      return getDposPerformance(businessId, period);
    },
    enabled: Boolean(businessId),
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useCancelDposSession = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!businessId) throw new Error("Business ID is required");
      return cancelDposSession(businessId, sessionId);
    },
  });
};

export const useDposCashierModeStatus = (businessId: string | null) => {
  return useQuery({
    queryKey: ["dposCashierModeStatus", businessId],
    queryFn: async () => {
      if (!businessId) {
        return {
          hasPin: false,
          pinLength: null,
          lockedUntil: null,
          isLocked: false,
          session: null,
        } as DposCashierModeStatus;
      }

      return getDposCashierModeStatus(businessId);
    },
    enabled: Boolean(businessId),
    refetchInterval: (query) =>
      query.state.data?.session?.status === "active" ? 60 * 1000 : false,
    staleTime: 1000 * 30,
  });
};

export const useSetupDposCashierPin = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (data: SetupDposCashierPinRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      return setupDposCashierPin(businessId, data);
    },
  });
};

export const useVerifyDposCashierPin = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (data: VerifyDposCashierPinRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      return verifyDposCashierPin(businessId, data);
    },
  });
};

export const useExitDposCashierMode = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (data: ExitDposCashierModeRequest = {}) => {
      if (!businessId) throw new Error("Business ID is required");
      return exitDposCashierMode(businessId, data);
    },
  });
};
